import { Box, Stack, Tooltip, TooltipProps, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";

/*
 Renders a display-only field with a tooltip that appears on hover over the label.
 Similar styling to InfoBox but without click/popover behavior.
*/
export const DisplayFieldWithTooltip = ({
  text,
  label,
  transparentBackground,
  tooltip,
  tooltipEnterDelay = 500,
  tooltipPlacement = "right",
  textColor,
  isItalic = false,
}: {
  text: string | undefined | null;
  label: string;
  transparentBackground?: boolean;
  tooltip?: ReactNode;
  tooltipEnterDelay?: number;
  tooltipPlacement?: TooltipProps["placement"];
  textColor?: string;
  isItalic?: boolean;
}) => {
  const theme = useTheme();

  const labelFontSize = "0.75rem";
  const valueFontSize = "1rem";

  const textContent = text ? (
    <Box>
      <Typography
        sx={{
          fontSize: valueFontSize,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: textColor,
          fontStyle: isItalic ? "italic" : "normal",
        }}
      >
        {text}
      </Typography>
    </Box>
  ) : (
    <Typography sx={{ fontSize: valueFontSize, minHeight: valueFontSize }} />
  );

  const labelContent = tooltip ? (
    <Tooltip title={tooltip} placement={tooltipPlacement} enterDelay={tooltipEnterDelay}>
      <Box component="span">{label}</Box>
    </Tooltip>
  ) : (
    label
  );

  const wrappedLabel = label && (
    <Typography
      variant="body2"
      color="text.secondary"
      fontSize={labelFontSize}
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {labelContent}
    </Typography>
  );

  return (
    <Box
      sx={{
        backgroundColor: transparentBackground ? "transparent" : theme.palette.grey50.main,
        // The boxed (grey) variant insets its content; the transparent variant
        // sits inline with sibling DisplayOnlyFields, which have no horizontal
        // padding, so it must not indent or its label/value fall out of alignment.
        px: transparentBackground ? 0 : 2,
        py: 0.5,
        borderRadius: 1,
        height: "100%",
        minHeight: 50,
        overflow: "hidden",
      }}
    >
      <Stack spacing={0}>
        {wrappedLabel}
        {textContent}
      </Stack>
    </Box>
  );
};
